import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useHeroItems, useProfile } from '../modules';

import { apiService, configService } from '../services';
import i18n from "../i18n";
import { CampaignModel, InteractionEventType } from 'outbound-api-client/lib/models';
import { LoaderAnimated } from '../components';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleRight } from '@fortawesome/free-solid-svg-icons';
import { Dropdown } from '../components/Dropdown';
import moment from 'moment';

const t = i18n.t;

export const CampaignFilter: React.FC = () => {
    const { HeroFeed } = useHeroItems();
    const { user } = useProfile(); // to get the user details including the profile data

    const [campaigns, setCampaigns] = useState<CampaignModel[]>();
    const [isLoading, setIsLoading] = useState(false);
    const [selectedLanguage, setSelectedLanguage] = useState(i18n.language);
    const [searchText, setSearchText] = useState("");


    // Log "Page View" Interaction
    useEffect(() => {
        try {
            let path = window.location.pathname;
            apiService.postInteraction(InteractionEventType.WebPageView, 'PageView', path);
        } catch (err) {
            console.log(err);
        }
    }, []);


    useEffect(() => {
        setIsLoading(true);
        apiService.instance.getCampaigns(selectedLanguage).then(res => {

            // // sort res by identifier
            // res.sort((a, b) => (a.identifier || "").localeCompare(b.identifier || ""));

            // sort res by dateStart descending
            res.sort((a, b) => {
                const dateA = a.dateStart ? new Date(a.dateStart) : new Date(0);
                const dateB = b.dateStart ? new Date(b.dateStart) : new Date(0);
                return dateB.getTime() - dateA.getTime();
            });

            setCampaigns(res);
        }).catch(err => {
            console.log(err)
        }).finally(() => {
            setIsLoading(false);
        });
    }, [selectedLanguage])


    const languageCodes = ["en", "fr", "it", "de", "es", "pl", "tr"]
    const generator = new Intl.DisplayNames([i18n.language], { type: 'language' });
    const languageList = languageCodes.map(code => {
        try {
            // We pass the [code] itself so it returns the name in that language
            let nativeName = generator.of(code) || code;
            const formattedName = nativeName.charAt(0).toUpperCase() + nativeName.slice(1);
            return {
                code: code,
                nativeName: formattedName
            };
        } catch (e) {
            // Fallback to the code if the browser can't resolve the name
            return { code: code, nativeName: code };
        }
    });





    let filteredCampaigns = campaigns;
    if (searchText && searchText.length > 0) {
        filteredCampaigns = campaigns?.filter(c => {
            const searchLower = searchText.toLowerCase();
            return (c.title && c.title.toLowerCase().includes(searchLower)) || (c.identifier && c.identifier.toLowerCase().includes(searchLower));
        });
    }

    let campaignList = <LoaderAnimated />;

    if (!isLoading && campaigns && campaigns.length > 0)
        campaignList = <>
            {filteredCampaigns?.map((campaign, iCampaign) => {
                const headerImage = (campaign.contentUris.find(u => u.type === "THUMBNAIL_TALL"))?.uri;
                return <div key={`campaign_${iCampaign}`} className='col-12 col-lg-6 mb-3'>
                    <div className='campaign_item card h-100'>

                        {user?.email.toLowerCase() === "shanem@out-bound.co.uk" && <div className='campaign_item p-3 d-flex justify-content-between flex-column'>
                            {moment(campaign.dateStart).format("DD MMM yyyy")} - {moment(campaign.dateExpires).format("DD MMM yyyy")}
                        </div>}

                        <img src={headerImage} alt={campaign.title || "card heading"} />
                        <div className='card-body d-flex justify-content-between flex-column'>
                            <div>
                                <div className='d-flex gap-3 justify-content-between align-items-start'>
                                    <h3>{campaign.title}</h3>
                                    <div className="badge badge-secondary">{campaign.languageCode && generator.of(campaign.languageCode)}</div>
                                </div>
                                {campaign.tags?.map((tag, iTag) => <div key={`campaign_${iCampaign}_tag_${iTag}`}>{tag}</div>)}
                            </div>
                            <div className='mt-3'>
                                <a href={`/campaign/${campaign.identifier}`}>
                                    {t("campaign_access_text")} <FontAwesomeIcon icon={faAngleRight} />
                                </a>
                            </div>
                        </div>
                    </div>

                </div>
            })}

            {/* <code><pre>{JSON.stringify(campaigns, null, 2)}</pre></code> */}
        </>


    if (!isLoading && campaigns && campaigns.length === 0)
        campaignList = <>{t("campaign_none_found")}</>



    // only include campaigns whose language is English ('en')
    var items = campaigns?.filter(c => (c.languageCode || '').toLowerCase() === i18n.language.toLowerCase())
        .map(c => ({ value: c.identifier || "", label: c.title || "" })) || [];


    return (
        <>
            <Helmet>
                <title>{configService.current().applicationTitle} | {t("page_title_campaigns")}</title>
            </Helmet>
            <div className='home-page'>
                <div className='featureed-container'>
                    <div className='feature-campaign-container'>
                        <div className='content'>
                            <h1 dangerouslySetInnerHTML={{ __html: t("home_welcome") }}></h1>
                            <hr />
                            <Link className='btn btn-primary' to={t("button_discover_link")}>{t("button_discover_text")} <FontAwesomeIcon icon={faAngleRight} /></Link>
                        </div>
                    </div>
                    <div className='carousel'>
                        <HeroFeed />
                    </div>
                </div>
                <main>
                    <div className='container'>
                        <h2>{t("campaigns_heading")}</h2>
                        <div className='row'>
                            <div className='col-12 col-md-4 mb-3'>
                                <div className='card p-4 mb-4'>
                                    <strong>{t("header_language")}</strong>
                                    {languageList.map(lang => (
                                        <label key={lang.code} className='cursor-pointer user-select-none'>
                                            <input
                                                type="radio"
                                                name="language"
                                                className='me-2'
                                                value={lang.code}
                                                checked={selectedLanguage === lang.code}
                                                onChange={(e) => setSelectedLanguage(e.target.value)} />
                                            {lang.nativeName}
                                        </label>
                                    ))}
                                    <label className='cursor-pointer user-select-none'>
                                        <input
                                            type="radio"
                                            name="language"
                                            className='me-2'
                                            value="*"
                                            checked={selectedLanguage === "*"}
                                            onChange={(e) => setSelectedLanguage(e.target.value)} />
                                        {t("language_selector_all")}
                                    </label>
                                </div>

                                {user?.email.toLowerCase() === "shanem@out-bound.co.uk" && <>

                                    <div className="alert alert-info" role="alert">
                                        <p className='text-center mb-3'>Only available to Shane for testing purposes.</p>

                                        <div className='card p-4 mb-4'>
                                            <strong>{t("campaign_search_heading")}</strong>
                                            <label className='cursor-pointer user-select-none'>
                                                <input type="text" className='form-control' placeholder={t("campaign_search_placeholder")} value={searchText} onChange={(e) => setSearchText(e.currentTarget.value)} />
                                            </label>
                                        </div>

                                        <div className='card p-4'>
                                            <strong>Searchable Dropdown</strong>
                                            <Dropdown
                                                options={items}
                                                value={searchText}
                                                setCount={(e) => {
                                                    const value = typeof e === 'string' ? e.split('_')[0] : (typeof e === 'function' ? (() => { try { return e(''); } catch { return ''; } })() : '');

                                                    setSearchText(value);
                                                }}
                                            />

                                            <select defaultValue={searchText} onChange={
                                                (e) => {
                                                    const value = e.target.value.split('_')[0];
                                                    setSearchText(value);
                                                }
                                            }>
                                                {items?.map((option) => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                </>}

                            </div>
                            <div className='col-12 col-md-8 mb-3'>
                                <div className='row'>
                                    {campaignList}
                                </div>
                            </div>
                        </div>


                    </div>
                </main>
            </div>
        </>
    )
};

export default CampaignFilter;